import React, { useState, useEffect } from 'react';
import { Box, Alert, Text, Button, Group, Badge, Transition } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Extend Window interface to include dataLayer
declare global {
    interface Window {
        dataLayer?: any[];
    }
}

interface ProfileCompletionAlertProps {
    missingFields: string[];
    onCompleteClick: () => void;
    userId?: string; // Optional user ID for personalized storage
}

// Constants for localStorage
const STORAGE_KEY_PREFIX = 'profile-alert';
const DISMISS_DURATION_DAYS = 7; // Alert will reappear after 7 days

// Enhanced profile completion alert with better styling and animations
const ProfileCompletionAlert: React.FC<ProfileCompletionAlertProps> = ({
    missingFields,
    onCompleteClick,
    userId = 'anonymous'
}) => {
    const isMobile = useMediaQuery('(max-width: 576px)');
    const [visible, setVisible] = useState<boolean>(false);

    const storageKey = `${STORAGE_KEY_PREFIX}-${userId}-${missingFields.sort().join('-')}`;

    useEffect(() => {
        const checkAlertStatus = () => {
            try {
                const storedData = localStorage.getItem(storageKey);
                if (storedData) {
                    const { dismissedUntil } = JSON.parse(storedData);
                    if (new Date().getTime() < dismissedUntil) {
                        setVisible(false);
                        return;
                    }
                }
                setVisible(missingFields.length > 0);
            } catch (error) {
                setVisible(missingFields.length > 0);
            }
        };

        checkAlertStatus();
    }, [missingFields, storageKey]);

    const handleDismiss = () => {
        try {
            const dismissedUntil = new Date().getTime() + (DISMISS_DURATION_DAYS * 24 * 60 * 60 * 1000);
            localStorage.setItem(storageKey, JSON.stringify({
                dismissedUntil,
                dismissedAt: new Date().getTime(),
                missingFieldsCount: missingFields.length
            }));
            setVisible(false);
        } catch (error) {
        }
    };

    if (!visible || missingFields.length === 0) {
        return null;
    }

    return (
        <Transition mounted={visible} transition="slide-up" duration={400} timingFunction="ease">
            {(styles) => (
                <Box
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        padding: isMobile ? '8px 12px' : '12px 16px',
                        background: 'linear-gradient(to top, rgba(255,255,255,0.98), rgba(255,255,255,0.92))',
                        backdropFilter: 'blur(8px)',
                        ...styles,
                    }}
                >
                    <Alert
                        title="Complete Your Profile"
                        color="blue"
                        radius="md"
                        withCloseButton
                        onClose={handleDismiss}
                    >
                        <Text size="sm">To enhance your experience, please complete the following fields:</Text>
                        <Group mt="sm">
                            {missingFields.map((field) => (
                                <Badge key={field} color="red" variant="filled">{field}</Badge>
                            ))}
                        </Group>
                        <Button mt="md" onClick={onCompleteClick} color="blue">Complete Profile</Button>
                    </Alert>
                </Box>
            )}
        </Transition>
    );
};

export { ProfileCompletionAlert };
export default ProfileCompletionAlert;
